import { useEffect } from 'react'
import { create } from 'zustand'

/**
 * Navne på de stumper af stien, der ikke kan læses.
 *
 * Brødkrummerne udledes af URL'en, og et projekt-id er ikke til at læse for
 * nogen. Siden, der viser projektet, kender navnet — den melder det ind her,
 * mens den er åben, og brødkrummerne slår op i det.
 *
 * Ét sted for alle sådanne id'er frem for en løsning kun til projekter: et
 * dokument eller en tegning åbnet på egen side vil have præcis det samme
 * problem.
 */
interface BreadcrumbLabelState {
  labels: Record<string, string>
  setLabel: (segment: string, label: string) => void
  clearLabel: (segment: string) => void
}

const useBreadcrumbLabelStore = create<BreadcrumbLabelState>((set) => ({
  labels: {},

  setLabel: (segment, label) =>
    set((state) =>
      state.labels[segment] === label
        ? state
        : { labels: { ...state.labels, [segment]: label } },
    ),

  clearLabel: (segment) =>
    set((state) => {
      if (!(segment in state.labels)) return state

      const { [segment]: _removed, ...rest } = state.labels

      return { labels: rest }
    }),
}))

export function useBreadcrumbLabels(): Record<string, string> {
  return useBreadcrumbLabelStore((state) => state.labels)
}

/**
 * Melder et navn ind for en stump af stien, så længe siden er åben.
 *
 * Navnet ryddes ved unmount. Ellers ville en gammel titel kunne stå tilbage,
 * hvis det samme id senere fik et andet navn — eller ikke længere fandtes.
 */
export function useBreadcrumbLabel(segment: string | undefined, label: string | undefined): void {
  const setLabel = useBreadcrumbLabelStore((state) => state.setLabel)
  const clearLabel = useBreadcrumbLabelStore((state) => state.clearLabel)

  useEffect(() => {
    if (!segment || !label) return

    setLabel(segment, label)

    return () => clearLabel(segment)
  }, [segment, label, setLabel, clearLabel])
}
